import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Shop PDP — endless scroll product experience (sections/apc-shop.liquid).
 *
 * One slide per product image; after a product's last image the next product
 * begins. Slides are rendered twice by Liquid; the scroll position is kept in
 * the middle half of the two sets by jumping one set length whenever it
 * drifts out — invisible because the sets are identical — so the loop is
 * endless in both directions. The active slide's product drives the receipt
 * panel and the thumbnail rail.
 *
 * @typedef {object} ApcShopRefs
 * @property {HTMLElement} track - The scroll-snap slide container.
 * @property {HTMLElement[]} [receipts] - Receipt detail panels, one per product.
 * @property {HTMLElement} [thumbRail] - The thumbnail rail (translated to follow the active slide).
 * @extends Component<ApcShopRefs>
 */
class ApcShopSection extends Component {
  requiredRefs = ['track'];

  #active = 0;
  /** Rail position of the active thumb (index into the 3x cloned rail). */
  #thumbPos = 0;
  /** Rail position a thumbnail click asked for — fixes the travel direction. @type {number | null} */
  #thumbTarget = null;
  /** Settles the size-list closing transition. @type {number | undefined} */
  #sizeCloseTimer;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {IntersectionObserver | undefined} */
  #preloader;
  /** @type {number | null} */
  #scrollRaf = null;
  /** True while a thumbnail jump animates (in-between slides collapsed). */
  #jumping = false;
  /** Degrees applied to the FTY logo so far — bumped once per active-product change. */
  #ftySpin = 0;
  /** Settles the in-flight thumbnail jump early. @type {(() => void) | null} */
  #finishJump = null;
  #desktop = window.matchMedia('(min-width: 64rem)');

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    const slides = this.#slides;
    if (slides.length < 2) return;

    // The rail holds 3 identical sets; the anchor starts on the middle one.
    this.#thumbPos = (this.refs.thumbRail?.children.length ?? 0) / 3;

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      root: this.refs.track,
      threshold: 0.6,
    });
    for (const slide of slides) this.#observer.observe(slide);

    // Preload lookahead: slide images are lazy, but native lazy loading only
    // kicks in right at the viewport edge, so a swipe could catch an image
    // mid-load. Flip images to eager while they're still a few viewports out.
    this.#preloader = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const image = entry.target.querySelector('img');
          if (image) image.loading = 'eager';
          this.#preloader?.unobserve(entry.target);
        }
      },
      { root: this.refs.track, rootMargin: '300%' }
    );
    for (const slide of slides) this.#preloader.observe(slide);

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('click', this.#onDocumentClick);

    // Start on the second set so there is scroll room behind the first
    // product — scrolling back from it continues into the end of the list.
    requestAnimationFrame(() => {
      const { track } = this.refs;
      const vertical = this.#desktop.matches;
      const setLength = (vertical ? track.scrollHeight : track.scrollWidth) / 2;
      if (vertical) track.scrollTop += setLength;
      else track.scrollLeft += setLength;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#observer?.disconnect();
    this.#preloader?.disconnect();
    this.#finishJump?.();
    this.refs.track.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('click', this.#onDocumentClick);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    clearTimeout(this.#sizeCloseTimer);
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.refs.track.querySelectorAll('.apc-shop__slide')]);
  }

  /** @type {IntersectionObserverCallback} */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = Number(/** @type {HTMLElement} */ (entry.target).dataset.productIndex);
      if (!Number.isNaN(index)) this.#setActive(index);
    }
  };

  /**
   * Wrap the scroll position to keep the loop endless in both directions:
   * the position lives in the middle of the two sets, so there is always
   * a good chunk of a set of room before either hard end.
   */
  #onScroll = () => {
    // While a jump has slides collapsed the set length is wrong — skip the
    // wrap check until the jump settles.
    if (this.#jumping) return;
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      const { track } = this.refs;
      const vertical = this.#desktop.matches;
      const pos = vertical ? track.scrollTop : track.scrollLeft;
      const setLength = (vertical ? track.scrollHeight : track.scrollWidth) / 2;
      let next = pos;
      // The wrap thresholds sit 0.3 sets away from where a jump lands
      // (0.65/1.35), so one wrap can never park the position right on the
      // opposite threshold — symmetric 0.5/1.5 bounds made the two wraps
      // oscillate mid-snap, visibly jumping between slides near the boundary.
      if (pos >= setLength * 1.65) next = pos - setLength;
      else if (pos < setLength * 0.35) next = pos + setLength;
      if (next !== pos) {
        if (vertical) track.scrollTop = next;
        else track.scrollLeft = next;
      }
    });
  };

  /**
   * @param {number} index
   */
  #setActive(index) {
    if (index === this.#active) return;
    const previous = this.#active;
    this.#active = index;

    // The FTY logo spins per product change instead of tracking scroll
    // position — the track scrolls internally but the page/body never does,
    // so there's no document scroll to drive a scroll-linked animation.
    if (!prefersReducedMotion()) {
      const logo = this.querySelector('.apc-shop__logo--fty');
      if (logo instanceof HTMLElement) {
        this.#ftySpin += 90;
        logo.style.setProperty('--apc-shop-fty-spin', `${this.#ftySpin}deg`);
      }
    }

    for (const receipt of this.refs.receipts ?? []) {
      const active = Number(receipt.dataset.index) === index;
      receipt.classList.toggle('is-active', active);
      // Close an open size popup on the way out (a picked size persists —
      // the size line takes part in the receipt swap animation).
      if (!active) this.#closeSizes(receipt);
    }

    const rail = this.refs.thumbRail;
    if (!rail) return;
    const thumbs = [...rail.children];
    const count = thumbs.length / 3;

    // A thumbnail click names the exact rail stop the user pointed at, so the
    // rail travels toward it (a thumb above scrolls up, below scrolls down).
    // Scroll-driven changes move by the shortest circular step instead, so the
    // rail keeps travelling through the last→first wrap instead of rewinding.
    const clicked = this.#thumbTarget == null ? null : thumbs[this.#thumbTarget];
    this.#thumbTarget = null;
    let pos;
    if (clicked instanceof HTMLElement && Number(clicked.dataset.index) === index) {
      pos = thumbs.indexOf(clicked);
    } else {
      let delta = index - previous;
      if (delta > count / 2) delta -= count;
      if (delta < -count / 2) delta += count;
      pos = this.#thumbPos + delta;
    }

    // Keep the anchor inside the middle set: jump a whole set length without
    // transition first — the sets are identical, so the jump is invisible.
    if (pos < count || pos >= count * 2) {
      const snap = pos < count ? count : -count;
      rail.style.transition = 'none';
      rail.style.setProperty('--apc-shop-active', String(this.#thumbPos + snap));
      void rail.offsetWidth;
      rail.style.transition = '';
      pos += snap;
    }
    this.#thumbPos = pos;
    rail.style.setProperty('--apc-shop-active', String(pos));

    thumbs.forEach((thumb, railIndex) => {
      if (!(thumb instanceof HTMLElement)) return;
      thumb.classList.toggle('is-active', railIndex === pos);
      thumb.dataset.dist = String(Math.min(Math.abs(railIndex - pos), 3));
    });
  }

  /**
   * Scroll to a product's first image (thumbnail click) — targets the
   * occurrence of the slide nearest the current scroll position, so the
   * track travels the short way around in either direction. The slides in
   * between are collapsed for the duration of the scroll, so the jump reads
   * as a single swipe straight to the product instead of flying through
   * every intermediate image.
   * @param {Event} event
   */
  goTo(event) {
    const target = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(target instanceof HTMLElement)) return;
    const index = Number(target.dataset.index);

    // Settle any jump still in flight before measuring for the next one.
    this.#finishJump?.();

    const { track } = this.refs;
    const vertical = this.#desktop.matches;
    let pos = vertical ? track.scrollTop : track.scrollLeft;
    const slides = this.#slides;

    const candidates = slides.filter(
      (slide) => Number(slide.dataset.productIndex) === index && slide.dataset.imageIndex === '0'
    );
    if (!candidates.length) return;

    // The track must travel the way the user pointed: a thumb above the
    // anchor scrolls up, below scrolls down — the rail follows via
    // #thumbTarget. Nearest-occurrence alone could head the other way near
    // the loop seam.
    const railIndex = this.refs.thumbRail ? [...this.refs.thumbRail.children].indexOf(target) : -1;
    const dir = railIndex < 0 ? 0 : Math.sign(railIndex - this.#thumbPos);
    if (dir) {
      this.#thumbTarget = railIndex;
      // No occurrence on that side: slide the (identical) sets over by one
      // set length first — invisible, same trick as the endless-loop wrap.
      const setLength = (vertical ? track.scrollHeight : track.scrollWidth) / 2;
      const offsets = candidates.map((slide) => (vertical ? slide.offsetTop : slide.offsetLeft));
      if (!offsets.some((offset) => (dir > 0 ? offset > pos : offset < pos))) {
        const shift = dir > 0 ? -setLength : setLength;
        if (vertical) track.scrollTop += shift;
        else track.scrollLeft += shift;
        pos += shift;
      }
    }

    let best = null;
    let bestDistance = Infinity;
    for (const slide of candidates) {
      const offset = vertical ? slide.offsetTop : slide.offsetLeft;
      if (dir > 0 && offset <= pos) continue;
      if (dir < 0 && offset >= pos) continue;
      const distance = Math.abs(offset - pos);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slide;
      }
    }
    if (!best) return;

    // The slide currently on screen: nearest offset to the scroll position.
    let currentIndex = 0;
    let currentDistance = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs((vertical ? slide.offsetTop : slide.offsetLeft) - pos);
      if (distance < currentDistance) {
        currentDistance = distance;
        currentIndex = i;
      }
    });

    const targetIndex = slides.indexOf(best);
    const lo = Math.min(currentIndex, targetIndex);
    const hi = Math.max(currentIndex, targetIndex);
    const between = slides.slice(lo + 1, hi);

    // Collapse the in-between slides so the target sits right next to the
    // current slide, keeping the current slide visually in place. Suspend
    // snap BEFORE mutating: with slides collapsing above the viewport,
    // mandatory snap re-anchors on the followed slide by itself, and the
    // drift correction below would then compensate a second time — the jump
    // overshoots past the target and the receipt swaps twice on the way back.
    track.style.scrollSnapType = 'none';
    const current = slides[currentIndex];
    const anchor = vertical ? current.offsetTop : current.offsetLeft;
    for (const slide of between) slide.style.display = 'none';
    const drift = (vertical ? current.offsetTop : current.offsetLeft) - anchor;
    if (drift) {
      if (vertical) track.scrollTop += drift;
      else track.scrollLeft += drift;
    }

    /** @type {number} */
    let raf;
    const finish = () => {
      this.#finishJump = null;
      cancelAnimationFrame(raf);
      // Restore the collapsed slides and re-anchor on the target in the same
      // frame — the layout shift happens entirely off-screen.
      for (const slide of between) slide.style.display = '';
      const offset = vertical ? best.offsetTop : best.offsetLeft;
      if (vertical) track.scrollTop = offset;
      else track.scrollLeft = offset;
      // Safe to re-enable snap: we're parked exactly on a snap point.
      track.style.scrollSnapType = '';
      this.#jumping = false;
    };
    this.#finishJump = finish;
    this.#jumping = true;

    const targetOffset = vertical ? best.offsetTop : best.offsetLeft;
    if (prefersReducedMotion()) {
      if (vertical) track.scrollTop = targetOffset;
      else track.scrollLeft = targetOffset;
      finish();
      return;
    }

    // Native smooth scrolling has a browser-fixed speed and curve; animate
    // the scroll ourselves with the same ease-out as the thumbnail rail
    // (easeOutQuint ≈ --apc-shop-ease) so the two glide together. Snap is
    // already suspended (above) so the animation's scroll writes stick.
    const from = vertical ? track.scrollTop : track.scrollLeft;
    const distance = targetOffset - from;
    const duration = 900;
    /** @type {number | undefined} */
    let startTime;
    const step = (/** @type {number} */ now) => {
      startTime ??= now;
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 5);
      const at = from + distance * eased;
      if (vertical) track.scrollTop = at;
      else track.scrollLeft = at;
      if (t < 1) raf = requestAnimationFrame(step);
      else finish();
    };
    raf = requestAnimationFrame(step);
  }

  /**
   * Swap the "Select size" line for the bracketed size list. While open the
   * receipt behaves like a popup: title and price hide, a click outside
   * closes it again.
   * @param {Event} event
   */
  toggleSizes(event) {
    const sizes = /** @type {HTMLElement} */ (event.target).closest('.apc-shop__sizes');
    if (!sizes) return;
    this.#openSizes(sizes);
  }

  /**
   * @param {Element} sizes
   */
  #openSizes(sizes) {
    const toggle = sizes.querySelector('.apc-shop__size-toggle');
    const list = sizes.querySelector('.apc-shop__size-list');
    if (!(toggle instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    sizes.closest('.apc-shop__receipt')?.classList.add('is-open');
    toggle.hidden = true;
    toggle.setAttribute('aria-expanded', 'true');
    list.hidden = false;
    /** @type {HTMLInputElement | null} */ (
      list.querySelector('input:checked:not(:disabled)') ?? list.querySelector('input:not(:disabled)')
    )?.focus();
  }

  /**
   * Close the popup state: title and price show again. Without a selection
   * the list collapses back to the "Select size" line; with one it stays,
   * collapsed to the picked value.
   * @param {Element} receipt
   */
  #closeSizes(receipt) {
    receipt.classList.remove('is-open');
    if (receipt.querySelector('.apc-shop__size.is-selected')) return;
    const toggle = receipt.querySelector('.apc-shop__size-toggle');
    const list = receipt.querySelector('.apc-shop__size-list');
    if (!(toggle instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    toggle.hidden = false;
    toggle.setAttribute('aria-expanded', 'false');
    list.hidden = true;
  }

  /**
   * Keep the "Cart n" badge in sync from the cart:update payload; when the
   * count changes it rolls over digital-clock style (styles in apc-shop.css).
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    const count = event.detail?.data?.itemCount;
    const badge = this.querySelector('.apc-shop__cart-count');
    if (typeof count !== 'number' || !badge) return;

    const next = count > 0 ? String(count) : '';
    const previous = badge.dataset.count ?? (badge.hidden ? '' : (badge.textContent?.trim() ?? ''));
    badge.dataset.count = next;
    badge.hidden = next === '';
    if (next === previous) return;

    if (next === '' || previous === '') {
      badge.textContent = next;
      return;
    }

    badge.innerHTML = `<span class="apc-cart-roll"><span>${previous}</span><span>${next}</span></span>`;
    badge.firstElementChild?.addEventListener('animationend', () => (badge.textContent = next), { once: true });
  };

  /** Outside click closes any open size popup. */
  #onDocumentClick = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (target?.closest('.apc-shop__receipt')) return;
    for (const receipt of this.refs.receipts ?? []) {
      if (receipt.classList.contains('is-open')) this.#closeSizes(receipt);
    }
  };

  /**
   * A size radio was picked (click or arrow key): the other sizes hide, only
   * the picked one stays between the brackets.
   * @param {Event} event
   */
  sizeSelected(event) {
    const label = /** @type {HTMLElement} */ (event.target).closest('.apc-shop__size');
    const receipt = label?.closest('.apc-shop__receipt');
    if (!(label instanceof HTMLElement) || !receipt) return;

    for (const other of receipt.querySelectorAll('.apc-shop__size.is-selected')) {
      other.classList.remove('is-selected');
    }
    label.classList.add('is-selected');

    this.#collapseSizes(receipt);
  }

  /**
   * Collapse the open list down to "( picked )": the unpicked sizes shrink to
   * width 0 while they fade, so the picked one glides left in real layout at
   * the same time they disappear, instead of waiting for the fade to finish
   * first. Each unpicked size also collapses its own reserved gap-margin
   * (see .apc-shop__size-list in CSS) — since that margin belongs to the
   * item itself rather than being a shared column-gap, collapsing it always
   * closes exactly that item's slot, regardless of which neighbour was
   * picked. A timer (not transitionend) settles the state — it survives
   * interruptions.
   * @param {Element} receipt
   */
  #collapseSizes(receipt) {
    const unpicked = /** @type {HTMLElement[]} */ ([...receipt.querySelectorAll('.apc-shop__size:not(.is-selected)')]);
    if (prefersReducedMotion() || !unpicked.length) {
      receipt.classList.add('is-picked');
      return;
    }
    clearTimeout(this.#sizeCloseTimer);
    const widths = unpicked.map((size) => size.offsetWidth);
    receipt.classList.add('is-closing');
    unpicked.forEach((size, i) => (size.style.width = `${widths[i]}px`));
    void receipt.clientWidth;
    for (const size of unpicked) size.style.width = '0px';
    // All entries collapse together (no stagger), so the settle timer only
    // needs to outlast the 400ms transition itself.
    this.#sizeCloseTimer = setTimeout(() => {
      receipt.classList.remove('is-closing');
      receipt.classList.add('is-picked');
      for (const size of unpicked) size.style.width = '';
    }, 420);
  }

  /**
   * Clicking (or Space on) the already-picked size reveals the other options
   * again; clicking it once more collapses back to just the picked value.
   * Selection changes are left to the radios' change event — click fires
   * before change, so a not-yet-selected label falls through here.
   * @param {Event} event
   */
  sizeClicked(event) {
    const label = /** @type {HTMLElement} */ (event.target).closest('.apc-shop__size');
    const receipt = label?.closest('.apc-shop__receipt');
    if (!(label instanceof HTMLElement) || !label.classList.contains('is-selected') || !receipt) return;

    if (receipt.classList.contains('is-picked')) {
      receipt.classList.remove('is-picked');
      // Re-revealing only restarts the stagger on the previously hidden
      // siblings; restart it on every entry (parens + picked size included)
      // so the whole "( S M L )" animates in together. Also shed any
      // leftovers from an interrupted closing transition.
      receipt.classList.remove('is-closing');
      const entries = receipt.querySelectorAll('.apc-shop__size, .apc-shop__size-paren');
      for (const entry of entries) {
        /** @type {HTMLElement} */ (entry).style.animation = 'none';
        /** @type {HTMLElement} */ (entry).style.width = '';
      }
      void label.offsetWidth;
      for (const entry of entries) /** @type {HTMLElement} */ (entry).style.animation = '';
    } else if (!receipt.classList.contains('is-closing')) {
      // Collapsing back animates the same way as picking a size did.
      this.#collapseSizes(receipt);
    }
  }

  /**
   * Add the selected variant to the cart, swap the receipt for a "Thank you"
   * line for 5 seconds, then restore it. The cart popup and shop badge
   * live-update via the cart:update event.
   * @param {Event} event
   */
  async purchase(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target).closest('button');
    const receipt = button?.closest('.apc-shop__receipt');
    if (!button || !receipt) return;

    const selected = /** @type {HTMLElement | null} */ (receipt.querySelector('.apc-shop__size.is-selected'));
    const variantId = selected?.dataset.variantId ?? /** @type {HTMLElement} */ (receipt).dataset.variantId;
    if (!variantId) {
      // No size picked yet: open the size list and nudge it.
      const sizes = receipt.querySelector('.apc-shop__sizes');
      if (sizes) {
        this.#openSizes(sizes);
        sizes.classList.remove('is-nudge');
        requestAnimationFrame(() => sizes.classList.add('is-nudge'));
      }
      return;
    }

    button.disabled = true;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      // add.js only returns the added lines; fetch the cart for the absolute
      // count — cart-icon.js treats a missing itemCount as 0 and hides the
      // header bubble, so the event must carry it.
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      document.dispatchEvent(new CartUpdateEvent(cart, 'apc-shop', { source: 'apc-shop', itemCount: cart.item_count }));
      receipt.classList.add('is-thanks');
      // 2s of "Thank you", then the cart drawer takes over.
      setTimeout(() => /** @type {any} */ (document.querySelector('apc-cart-popup'))?.showDialog(), 2000);
      setTimeout(() => {
        receipt.classList.remove('is-thanks');
        this.#closeSizes(receipt);
        button.disabled = false;
      }, 5000);
    } catch (error) {
      // ponytail: no inline error UI yet — re-enable and log. Add a message
      // line to the receipt if sold-out races show up in practice.
      console.error(error);
      button.disabled = false;
    }
  }
}

if (!customElements.get('apc-shop-section')) {
  customElements.define('apc-shop-section', ApcShopSection);
}
